import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import ResizeObserver from 'resize-observer-polyfill';

import { isSSR } from '@telialt/utils/src/helpers';
import {
    NAVIGATION_OFFSET_CSS_VARIABLE,
    NAVIGATION_OFFSET_TRANSITION_CSS_VARIABLE,
} from '@telialt/utils/src/NavigationUtils/constants';
import { isMobileOrTabletRes } from '@telialt/utils/src/responsive';

import { NAVIGATION_CONTEXT_BAR_HEIGHT, NAVIGATION_MAIN_BAR_HEIGHT } from '../constants';

type NavigationState = {
    offset: number;
    isFixed: boolean;
};

const ANIMATION_DURATION = 200;

export const useStickyNavigation = (hasTopBar: boolean) => {
    const [topBarHeight, setTopBarHeight] = useState(hasTopBar ? NAVIGATION_CONTEXT_BAR_HEIGHT : 0);
    const [topBarHeightMobile, setTopBarHeightMobile] = useState(0);
    const scrollYRef = useRef(isSSR() ? 0 : window.scrollY);
    const resizeObserverRef = useRef<ResizeObserver | null>(null);
    const mutationObserverRef = useRef<MutationObserver | null>(null);
    const mountRef = useRef(false);
    const scrollDirectionRef = useRef<'up' | 'down'>('up');
    const navigationHeight = topBarHeight + NAVIGATION_MAIN_BAR_HEIGHT;
    const navigationHeightMobile = topBarHeightMobile + NAVIGATION_MAIN_BAR_HEIGHT;
    const skipScrollTriggerRef = useRef(false);
    const [isMobileView, setIsMobileView] = useState(false);

    const [value, setValue] = useState<NavigationState>({
        offset: 0,
        isFixed: false,
    });

    const setNavigationBannerRef = useCallback((element: HTMLDivElement | null) => {
        resizeObserverRef.current?.disconnect();
        mutationObserverRef.current?.disconnect();

        if (element) {
            const updateTopBarHeight = () => {
                const { height } = element.getBoundingClientRect();

                skipScrollTriggerRef.current = true;
                setTopBarHeight(height);
                setTopBarHeightMobile(height);
            };
            const resizeObserver = new ResizeObserver(updateTopBarHeight);
            resizeObserver.observe(element);
            resizeObserverRef.current = resizeObserver;

            const mutationObserver = new MutationObserver(updateTopBarHeight);
            mutationObserver.observe(element, {
                childList: true,
                subtree: true,
            });
            mutationObserverRef.current = mutationObserver;
        }
    }, []);

    useEffect(() => {
        setIsMobileView(isMobileOrTabletRes());

        const handleResize = () => {
            setIsMobileView(isMobileOrTabletRes());
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            resizeObserverRef.current?.disconnect();
        };
    }, []);

    useEffect(() => {
        const computeValue = (prevState: NavigationState, initial: boolean): NavigationState => {
            const isScrolledPastTopBar = window.scrollY >= topBarHeight;
            const isScrolledPastMainBar = window.scrollY >= topBarHeight + NAVIGATION_MAIN_BAR_HEIGHT;
            const isScrollingDown = scrollDirectionRef.current === 'down';
            const isScrollingUp = scrollDirectionRef.current === 'up';

            if (initial) {
                return { offset: isMobileOrTabletRes() ? topBarHeightMobile : topBarHeight, isFixed: true };
            }

            if (isMobileView) {
                if (!isScrolledPastTopBar) {
                    if (prevState.isFixed && isScrollingUp) {
                        return { offset: topBarHeight, isFixed: true };
                    }

                    return { isFixed: false, offset: Math.max(topBarHeight - window.scrollY, 0) };
                }

                if (isScrolledPastMainBar && isScrollingDown) {
                    return { offset: -NAVIGATION_MAIN_BAR_HEIGHT, isFixed: true };
                }

                if (isScrollingUp) {
                    return { offset: topBarHeight, isFixed: true };
                }

                return { offset: 0, isFixed: true };
            }

            if (!isScrolledPastTopBar) {
                if (prevState.isFixed && isScrollingUp) {
                    return { offset: topBarHeight, isFixed: true };
                }

                return { offset: 0, isFixed: false };
            }

            return {
                offset: isScrollingUp ? topBarHeight : 0,
                isFixed: true,
            };
        };

        const handleScroll = () => {
            if (skipScrollTriggerRef.current) {
                skipScrollTriggerRef.current = false;

                return;
            }

            setValue((prevState) => computeValue(prevState, false));

            scrollDirectionRef.current = (() => {
                if (window.scrollY > scrollYRef.current) {
                    return 'down';
                }
                if (window.scrollY < scrollYRef.current) {
                    return 'up';
                }

                return scrollDirectionRef.current;
            })();

            scrollYRef.current = window.scrollY;
        };

        setValue((prevState) => computeValue(prevState, true));

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => window.removeEventListener('scroll', handleScroll);
    }, [topBarHeight, topBarHeightMobile, isMobileView]);

    const navigationOffset = useMemo(
        () =>
            isMobileView && Math.abs(value.offset) === NAVIGATION_MAIN_BAR_HEIGHT
                ? 0
                : value.offset + NAVIGATION_MAIN_BAR_HEIGHT,
        [isMobileView, value.offset],
    );

    useEffect(() => {
        document.documentElement.style.setProperty(NAVIGATION_OFFSET_CSS_VARIABLE, `${navigationOffset}px`);
    }, [navigationOffset]);

    useEffect(() => {
        document.documentElement.style.setProperty(
            NAVIGATION_OFFSET_TRANSITION_CSS_VARIABLE,
            value.isFixed ? 'top var(--navigation-offset-duration, 150ms) ease-in-out' : 'unset',
        );
    }, [value.isFixed]);

    useEffect(() => {
        return () => {
            document.documentElement.style.removeProperty(NAVIGATION_OFFSET_CSS_VARIABLE);
            document.documentElement.style.removeProperty(NAVIGATION_OFFSET_TRANSITION_CSS_VARIABLE);
        };
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            mountRef.current = true;
        }, ANIMATION_DURATION);

        return () => clearTimeout(timer);
    }, []);

    const topBarOffset = useMemo(() => {
        if (isMobileView && value.offset === -NAVIGATION_MAIN_BAR_HEIGHT) {
            return -topBarHeight;
        }

        if (value.offset === 0 && topBarHeight > NAVIGATION_CONTEXT_BAR_HEIGHT) {
            return -topBarHeight + NAVIGATION_MAIN_BAR_HEIGHT;
        }

        return 0;
    }, [value.offset, topBarHeight, isMobileView]);

    return {
        setNavigationBannerRef,
        height: navigationHeight,
        heightMobile: navigationHeightMobile,
        mainBarOffset: value.offset,
        topBarOffset,
        position: value.isFixed ? 'fixed' : 'static',
        duration: mountRef.current ? ANIMATION_DURATION : 0,
    };
};
